import React, { useState, useEffect } from "react";
import {
  Box,
  Chip,
  Stack,
  Tooltip,
  CircularProgress,
  Typography,
} from "@mui/material";
import { FileIcon, Download, Paperclip } from "lucide-react";
import { useGraphApi } from "../../../store/graph/useGraphApi";

interface Attachment {
  id: string;
  name: string;
  contentType: string;
  size: number;
}

interface EmailAttachmentsProps {
  messageId: string;
}

export const EmailAttachments: React.FC<EmailAttachmentsProps> = ({
  messageId,
}) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { callGraphApi, downloadGraphResource } = useGraphApi(); // Använd den nya hooken
  
  console.log("EmailAttachmentsProps", messageId);
  
  useEffect(() => {
    const fetchAttachments = async () => {
      try {
        setLoading(true);
        const data = await callGraphApi(`/me/messages/${messageId}/attachments`);
        setAttachments(data.value);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load attachments"
        );
      } finally {
        setLoading(false);
      }
    };

    if (messageId) {
      fetchAttachments();
    }
  }, [messageId, callGraphApi]);

  const handleDownload = async (attachmentId: string, fileName: string) => {
    try {
      // Använd downloadGraphResource för att hämta filen
      const blob = await downloadGraphResource(`/me/messages/${messageId}/attachments/${attachmentId}/$value`);
      
      // Skapa en nedladdningslänk
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to download attachment"
      );
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Paperclip size={16} />
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (error) {
    return (
      <Tooltip title={error}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "error.main",
          }}
        >
          <Paperclip size={16} />
          <Typography variant="caption">Kunde inte ladda bilagor</Typography>
        </Box>
      </Tooltip>
    );
  }

  if (attachments.length === 0) {
    return null;
  }

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      {attachments.map((attachment) => (
        <Chip
          key={attachment.id}
          icon={<FileIcon size={14} />}
          label={`${attachment.name} (${formatFileSize(attachment.size)})`}
          variant="outlined"
          size="small"
          onDelete={() => handleDownload(attachment.id, attachment.name)}
          deleteIcon={
            <Tooltip title="Ladda ner">
              <Download size={14} />
            </Tooltip>
          }
        />
      ))}
    </Stack>
  );
};